<template>
  <Transition name="load-fade">
    <div v-if="!firstLoadDone" class="loading-overlay">
      <img class="loading-logo" src="/images/terrassa-logo.png" />
      <p class="loading-text">Carregant presentació...</p>
    </div>
  </Transition>
  <div class="status-footer">
    <span class="status-dot" :class="'status-dot--' + statusType"></span>
    <span class="status-label">{{ statusMessage }}</span>
  </div>
  <SlideShow
    v-show="firstLoadDone"
    :diapositivas="diapos"
    :idVetlla="idVetlla"
  />
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref } from "vue";

import axios from "axios";

import { Vetlla, Diapositiva, Sala } from "../types/vetlla.ts";
import useErpApiClient from "../erp-api-client";

import SlideShow from "../components/SlideShow.vue";
import "./PresentationSalaView.css";

const EMPTY_SLIDE: Diapositiva = {
  Id: -1,
  PathFoto: "",
  Missatge: "",
  Signatura: "",
  IsVisible: true,
};

function buildTitleSlide(nombre: string): Diapositiva {
  return { ...EMPTY_SLIDE, Missatge: nombre };
}

const IDVETLLA_UNKNOWN = -2;
const SALA_VACIA = -1;
const POLL_INTERVAL_MS = 5000;

function nowTime() {
  return new Date().toLocaleTimeString("ca-ES", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default defineComponent({
  components: { SlideShow },
  props: {
    idSala: {
      required: true,
      type: Number,
    },
  },
  setup(props) {
    const erpClient = useErpApiClient();

    const idVetlla = ref(IDVETLLA_UNKNOWN);
    const diapos = ref<Diapositiva[]>([]);
    const firstLoadDone = ref(false);
    const statusMessage = ref("Carregant...");
    const statusType = ref<"loading" | "ok" | "error">("loading");

    let intervalId: ReturnType<typeof setInterval>;

    function setStatus(type: "loading" | "ok" | "error", msg: string) {
      statusType.value = type;
      statusMessage.value = msg;
    }

    function markLoaded() {
      if (!firstLoadDone.value && diapos.value.length > 0) {
        firstLoadDone.value = true;
      }
    }

    function recoverFromError(msg: string) {
      setStatus("error", msg);
      if (diapos.value.length === 0) diapos.value = [EMPTY_SLIDE];
      markLoaded();
    }

    async function fetchDiapos(vetllaId: number, nombreDifunto: string) {
      try {
        const response = await axios.get(`/api/vetlla/read?id=${vetllaId}`);
        const vetlla = response.data as Vetlla;
        const visibleDiapos = vetlla.Diapositivas.filter((d) => d.IsVisible);

        diapos.value = [
          buildTitleSlide(vetlla.NombreDifunto),
          ...visibleDiapos,
        ];
        markLoaded();
        setStatus(
          "ok",
          `${vetlla.NombreDifunto} · ${visibleDiapos.length} diapos. · ${nowTime()}`,
        );
      } catch {
        diapos.value = [buildTitleSlide(nombreDifunto)];
        markLoaded();
        setStatus("ok", `${nombreDifunto} · Sense diapositives · ${nowTime()}`);
      }
    }

    async function updateDiapos() {
      try {
        const response = await erpClient.request(() =>
          axios.get("/api/sala/index"),
        );
        const salas: Sala[] = response.data;
        const sala = salas.find((s) => props.idSala == s.Id);

        if (!sala) {
          recoverFromError(`No s'ha trobat la sala ${props.idSala}`);
          return;
        }

        idVetlla.value = sala.IdVetlla;

        if (sala.IdVetlla === SALA_VACIA) {
          diapos.value = [EMPTY_SLIDE];
          markLoaded();
          setStatus("ok", `Sala sense vetlla assignada · ${nowTime()}`);
          return;
        }

        await fetchDiapos(sala.IdVetlla, sala.NombreDifunto);
      } catch {
        recoverFromError("Error de connexió amb el servidor");
      } finally {
        sendHeartbeat();
      }
    }

    function sendHeartbeat() {
      axios
        .post("/api/sala/heartbeat", {
          idSala: Number(props.idSala),
          status: statusType.value,
          message: statusMessage.value,
        })
        .catch(() => {});
    }

    onMounted(() => {
      updateDiapos();
      intervalId = setInterval(updateDiapos, POLL_INTERVAL_MS);
    });

    onBeforeUnmount(() => {
      clearInterval(intervalId);
    });

    return {
      diapos,
      idVetlla,
      firstLoadDone,
      statusMessage,
      statusType,
    };
  },
});
</script>
